import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/ConductExam.css";

const ConductExam = () => {
  const [timer, setTimer] = useState(180 * 60);
  const [examStarted, setExamStarted] = useState(false);
  const [examEnded, setExamEnded] = useState(false);

  // Sound detection
  const [soundDetected, setSoundDetected] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const recordingInterval = useRef(null);

  // Gaze detection
  const [gazeStatus, setGazeStatus] = useState("Initializing...");
  const [isSuspicious, setIsSuspicious] = useState(false);
  const [suspiciousCounter, setSuspiciousCounter] = useState(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const gazeIntervalId = useRef(null);
  const videoStreamRef = useRef(null);

  // Warning + violations
  const [warningMessage, setWarningMessage] = useState("");
  const [violationCount, setViolationCount] = useState(0);

  const location = useLocation();
  const navigate = useNavigate();

  const examTitle = location.state?.examTitle || "Unknown Exam";
  const studentEmail = localStorage.getItem("email") || null;

  // ===================================================
  // 🔐 LOGIN + EXAM CHECKS (Three cases)
  // ===================================================
  useEffect(() => {
    // Case 1: Neither logged in nor exam chosen
    if (!studentEmail && examTitle === "Unknown Exam") {
      alert("⚠️ Please login and select your exam before proceeding!");
      navigate("/login");
      return;
    }

    // Case 2: Exam chosen but not logged in
    if (!studentEmail && examTitle !== "Unknown Exam") {
      alert("⚠️ Please login to continue your selected exam!");
      navigate("/login");
      return;
    }

    // Case 3: Logged in but no exam chosen
    if (studentEmail && examTitle === "Unknown Exam") {
      alert("⚠️ Please select an exam from the recommendations page!");
      navigate("/recommendation");
      return;
    }
  }, [studentEmail, examTitle, navigate]);

  // ===================================================
  // Helper: Trigger warning
  // ===================================================
  const triggerWarning = (message) => {
    console.warn(message);
    setWarningMessage(message);
    setViolationCount((prev) => prev + 1);
    setTimeout(() => setWarningMessage(""), 6000);
  };

  // ===================================================
  // Timer
  // ===================================================
  useEffect(() => {
    if (!examStarted || examEnded) return;
    const timerInterval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timerInterval);
  }, [examStarted, examEnded]);

  // ===================================================
  // Sound Detection
  // ===================================================
  useEffect(() => {
    if (examStarted && !examEnded) startSoundCapture();
    return () => stopSoundCapture();
  }, [examStarted, examEnded]);

  // ===================================================
  // Gaze Detection
  // ===================================================
  useEffect(() => {
    if (examStarted && !examEnded) startGazeTracking();
    return () => stopGazeTracking();
  }, [examStarted]);

  // ===================================================
  // Cleanup
  // ===================================================
  useEffect(() => {
    return () => {
      stopSoundCapture();
      stopGazeTracking();
    };
  }, []);

  // ===================================================
  // Tab Switch Detection
  // ===================================================
  useEffect(() => {
    let leftTime = null;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        leftTime = Date.now();
        fetch("http://localhost:5000/api/tab-switch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: studentEmail,
            details: "User switched to another tab",
          }),
        });
      } else if (leftTime) {
        const awayDuration = ((Date.now() - leftTime) / 1000).toFixed(1);
        triggerWarning(`⚠️ You switched tabs! (${awayDuration}s away)`);
        leftTime = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [studentEmail]);

  // ===================================================
  // Copy / Paste Detection
  // ===================================================
  useEffect(() => {
    const handleCopy = (e) => {
      e.preventDefault();
      triggerWarning("⚠️ Copying is disabled during the exam!");
      fetch("http://localhost:5000/api/violation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "copy",
          email: studentEmail,
          details: "Tried to copy text",
        }),
      });
    };

    const handlePaste = (e) => {
      e.preventDefault();
      triggerWarning("⚠️ Pasting is disabled during the exam!");
      fetch("http://localhost:5000/api/violation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "paste",
          email: studentEmail,
          details: "Tried to paste content",
        }),
      });
    };

    document.addEventListener("copy", handleCopy);
    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("paste", handlePaste);
    };
  }, [studentEmail]);

  // ===================================================
  // === SOUND CAPTURE ===
  // ===================================================
  const getSupportedMimeType = () => {
    const types = ["audio/webm", "audio/webm;codecs=opus"];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return "";
  };

  const startSoundCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const supportedType = getSupportedMimeType();
      const options = supportedType ? { mimeType: supportedType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        if (audioChunksRef.current.length === 0) return;

        const blob = new Blob(audioChunksRef.current, { type: options.mimeType || "audio/webm" });
        audioChunksRef.current = [];

        const formData = new FormData();
        formData.append("audio", blob, "recording.webm");

        try {
          const resp = await fetch("http://localhost:5000/detect_sound", { method: "POST", body: formData });
          // If the request itself failed or server returned non-2xx, bail safely
          if (!resp.ok) {
            console.warn("detect_sound responded with", resp.status);
            return;
          }

          const result = await resp.json().catch(() => ({}));

          setSoundDetected(!!result.sound_detected);

          if (result.sound_detected) {
            // guard: only call toFixed if it's a number
            const hasNumber = typeof result.loudness_dBFS === "number";
            const loudText = hasNumber ? `${result.loudness_dBFS.toFixed(2)} dBFS` : "unknown loudness";

            triggerWarning("⚠️ Background noise detected!");

            await fetch("http://localhost:5000/api/violation", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                type: "sound",
                email: studentEmail,
                details: `Detected sound: ${loudText}`,
              }),
            });
          }
        } catch (e) {
          console.warn("detect_sound fetch failed:", e);
          // no throw — just skip using result.loudness_dBFS
        }
      };

      mediaRecorder.start();

      recordingInterval.current = setInterval(() => {
        if (mediaRecorder.state === "recording") {
          mediaRecorder.stop();
          setTimeout(() => mediaRecorder.start(), 200);
        }
      }, 3000);
    } catch {
      triggerWarning("⚠️ Microphone access error!");
    }
  };

  const stopSoundCapture = () => {
    if (recordingInterval.current) clearInterval(recordingInterval.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") mediaRecorderRef.current.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
  };

  // ===================================================
  // === GAZE TRACKING ===
  // ===================================================
  const startGazeTracking = async () => {
    try {
      if (videoStreamRef.current) {
        console.log("Camera already in use, reusing existing stream.");
        videoRef.current.srcObject = videoStreamRef.current;
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, // explicitly request front camera
        audio: false
      });
      videoStreamRef.current = stream;
      videoRef.current.srcObject = stream;
      gazeIntervalId.current = setInterval(sendFrame, 500);
    } catch (err) {
      triggerWarning("⚠️ Webcam access denied!");
      setGazeStatus("Error: Webcam access denied");
    }
  };

  const stopGazeTracking = () => {
    if (gazeIntervalId.current) clearInterval(gazeIntervalId.current);
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((track) => track.stop());
      videoStreamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const sendFrame = () => {
    const video = videoRef.current;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/jpeg", 0.7);

    fetch("http://localhost:8000/predict_gaze", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image: imageData }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.label === "⚠️ Multiple Faces Detected!") {
          triggerWarning("⚠️ Multiple people detected in frame!");
        } else if (data.suspicious) {
          triggerWarning("⚠️ Please look at the screen!");
        }

        setGazeStatus(data.label);
        setIsSuspicious(data.suspicious);
        setSuspiciousCounter((prev) => prev + (data.suspicious ? 1 : 0));
      })
      .catch((err) => console.error("Gaze detection error:", err));
  };

  const formatTime = (seconds) => {
    const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  return (
    <div className="conduct-exam-container">
      {warningMessage && <div className="warning-banner">{warningMessage}</div>}

      {examStarted && !examEnded && (
        <div className="violation-counter">
          ⚠️ Violations: <strong>{violationCount}</strong>
        </div>
      )}

      {!examStarted && !examEnded ? (
        <>
          <h1 className="exam-title">Exam Title: {examTitle}</h1>
          <p className="student-info">
            👤 Logged in as: <strong>{studentEmail}</strong>
          </p>
          <button className="start-exam-btn" onClick={() => setExamStarted(true)}>
            Start Exam
          </button>
        </>
      ) : examEnded ? (
        <h2 className="exam-ended-message">Exam Submitted ✅</h2>
      ) : (
        <>
          <h1 className="exam-title">{examTitle}</h1>
          <h2 className="exam-timer">{formatTime(timer)}</h2>

          <div className="main-proctoring-layout">
            <div className="right-panel">
              <div className="video-container">
                <video ref={videoRef} autoPlay muted playsInline className="proctoring-video"></video>
                <canvas ref={canvasRef} className="proctoring-canvas"></canvas>
              </div>
            </div>
          </div>

          <button
            className="end-exam-btn"
            onClick={() => {
              setExamEnded(true);
              setExamStarted(false);
              stopSoundCapture();
              stopGazeTracking();
            }}
          >
            End Exam
          </button>
        </>
      )}
    </div>
  );
};

export default ConductExam;
